"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { mentorshipApi } from "@/lib/api/services/mentorship.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, UserPlus, Clock, Loader2 } from "lucide-react";
import Link from "next/link";

interface Request {
  id: string;
  menteeId: string;
  menteeName: string;
  topic: string;
  status: string;
  createdAt: string;
}

export default function MentorshipRequestsPage() {
  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ["mentorship-requests", "incoming"],
    queryFn: async () => {
      const data = await mentorshipApi.listRequests("incoming");
      return ((data as unknown as { data?: Request[] }).data ?? []) as Request[];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Mentorship Requests</h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 w-full bg-muted/10 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Mentorship Requests</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="mx-auto h-12 w-12 text-destructive/50 animate-spin" />
            <h3 className="mt-4 text-lg font-medium text-foreground">Failed to load requests</h3>
            <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/mentorship">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-foreground">Mentorship Requests</h1>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserPlus className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium text-foreground">No requests</h3>
            <p className="mt-2 text-sm text-muted-foreground">No mentorship requests to review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card key={req.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{req.menteeName}</p>
                  <p className="text-xs text-muted-foreground">Topic: {req.topic}</p>
                  <p className="text-xs text-muted-foreground">{req.createdAt}</p>
                </div>
                <Badge variant={req.status === "pending" ? "outline" : "secondary"} className="capitalize">
                  {req.status}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}